import { z } from 'zod'

// ============================================================================
// Demo Profile Status
// ============================================================================

export const DemoProfileStatus = {
  PENDING_VERIFICATION: 'pending_verification',
  VERIFIED: 'verified',
  CREATING: 'creating',
  READY: 'ready',
  EXPIRED: 'expired',
  FAILED: 'failed',
} as const

export const demoProfileStatusSchema = z.enum([
  DemoProfileStatus.PENDING_VERIFICATION,
  DemoProfileStatus.VERIFIED,
  DemoProfileStatus.CREATING,
  DemoProfileStatus.READY,
  DemoProfileStatus.EXPIRED,
  DemoProfileStatus.FAILED,
])
export type DemoProfileStatusType = z.infer<typeof demoProfileStatusSchema>

export const DemoProfileSource = {
  QR_CODE: 'qr_code',
} as const

// ============================================================================
// Demo Profile
// ============================================================================

export const demoProfileSchema = z.object({
  demoProfileId: z.uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  websiteUrl: z.string().nullish(),
  businessType: z.string().nullish(),
  scrapedData: z.unknown().nullish(),
  status: demoProfileStatusSchema,
  verificationToken: z.string().nullish(),
  corporateId: z.uuid().nullish(),
  firebaseUid: z.string().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
  expiresAt: z.date(),
  source: z.string().nullish(),
})
export type DemoProfile = z.infer<typeof demoProfileSchema>

// ============================================================================
// API Request/Response Schemas
// ============================================================================

export const demoSignupInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  websiteUrl: z
    .string()
    .max(500)
    .transform((val) => {
      const trimmed = val.trim()
      if (!trimmed) return undefined
      // Accept bare domains (e.g. "mbdy.be") — normalize to full URL
      return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`
    })
    .optional(),
  businessType: z.string().max(50).optional(),
  recaptchaToken: z.string().optional(),
  source: z.string().max(50).optional(),
})
export type DemoSignupInput = z.infer<typeof demoSignupInputSchema>

export const demoSignupResponseSchema = z.union([
  z.object({
    demoProfileId: z.uuid(),
    requiresVerification: z.literal(true),
  }),
  z.object({
    existingDemo: z.literal(true),
    magicLinkSent: z.literal(true),
  }),
])
export type DemoSignupResponse = z.infer<typeof demoSignupResponseSchema>

export const demoVerifyInputSchema = z.object({
  token: z.string().min(1),
  demoProfileId: z.uuid(),
})
export type DemoVerifyInput = z.infer<typeof demoVerifyInputSchema>

export const demoVerifyResponseSchema = z.object({
  verified: z.literal(true),
  customToken: z.string(),
  demoProfileId: z.uuid(),
})
export type DemoVerifyResponse = z.infer<typeof demoVerifyResponseSchema>

export const demoStatusResponseSchema = z.object({
  status: demoProfileStatusSchema,
  progress: z.number().optional(),
  steps: z.array(z.object({ name: z.string(), complete: z.boolean() })).optional(),
})
export type DemoStatusResponse = z.infer<typeof demoStatusResponseSchema>

export const demoMagicLinkInputSchema = z.object({
  email: z.string().email(),
})
export type DemoMagicLinkInput = z.infer<typeof demoMagicLinkInputSchema>

export const demoResendVerificationInputSchema = z.object({
  demoProfileId: z.uuid(),
})
export type DemoResendVerificationInput = z.infer<typeof demoResendVerificationInputSchema>
